/**
 * The client portal map's reads and the staff choice (Diurnal parity 03-053, 05-025; docs/specs/portal/client-area.md
 * §11; 20261027012000_portal_map.sql). The client (and Preview as client, with the staff member's sign-in) reads the
 * project's aircraft and vehicles through `portal_tracking_assets` and `portal_tracking_positions`, and its hazards
 * through the `portal-hazards` Edge Function: the database decides who sees what, so a preview shows the client's rows
 * exactly. Staff read the project's choice and its pilot hazard pack through their own policies and change the choice
 * with `portal_map_set`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  PORTAL_HAZARDS_FUNCTION,
  type PortalAssetRow,
  type PortalHazardsAnswer,
  type PortalPositionRow,
} from '@teslyn/domain';
import { all, plainPortalError, type Res } from './api.ts';

export interface PortalTrackingRows {
  readonly assets: PortalAssetRow[];
  readonly rows: PortalPositionRow[];
}

/** The aircraft and vehicles the client may see, and their fixes of the last `hours` (1 to 48). */
export async function loadPortalTracking(
  sb: SupabaseClient,
  projectId: string,
  hours: number,
): Promise<PortalTrackingRows> {
  const [assets, rows] = await Promise.all([
    (async () => {
      const r = (await sb.rpc('portal_tracking_assets', { p_project: projectId })) as Res<PortalAssetRow[]>;
      if (r.error) throw new Error(plainPortalError(r.error));
      return r.data ?? [];
    })(),
    all<PortalPositionRow>(
      (from, to) =>
        sb
          .rpc('portal_tracking_positions', { p_project: projectId, p_hours: hours })
          .range(from, to) as unknown as PromiseLike<Res<PortalPositionRow[]>>,
    ),
  ]);
  return { assets, rows };
}

/** The hazards of the client's map (none until staff publish them). */
export async function loadPortalHazards(sb: SupabaseClient, projectId: string): Promise<PortalHazardsAnswer> {
  const { data, error } = await sb.functions.invoke(PORTAL_HAZARDS_FUNCTION, {
    body: { project_id: projectId },
  });
  if (error) {
    // the function's own words when it gave some
    const context = (error as { context?: Response }).context;
    const body =
      context && typeof context.json === 'function' ? await context.json().catch(() => null) : null;
    const words = body && typeof body === 'object' ? (body as { error?: unknown }).error : null;
    throw new Error(
      typeof words === 'string' ? words : 'The hazards could not be loaded. Try again shortly.',
    );
  }
  return data as PortalHazardsAnswer;
}

/** The project's choice, as staff read it (null: never chosen, so nothing is published). */
export interface PortalMapSetting {
  readonly hazardsPublished: boolean;
  readonly landholdersShared: boolean;
  readonly updatedAt: string | null;
  readonly updatedByName: string;
}

/** The project's hazard pack the hazards come from (parity C5), as staff read it, and the copy the client has. */
export interface ProjectPack {
  readonly name: string;
  readonly version: number;
  readonly publishedAt: string;
  readonly publishedByName: string;
  readonly properties: number;
  /** The version the client sees, or null. */
  readonly clientVersion: number | null;
}

interface SettingRow {
  hazards_published: boolean;
  landholders_shared: boolean;
  updated_at: string | null;
  updated_by_name: string | null;
}

interface PackRow {
  name: string;
  version: number;
  published_at: string;
  published_by_name: string | null;
  stats: { properties?: unknown } | null;
  hazard_pack_release?: readonly { audience: string; version: number }[] | null;
}

export async function loadPortalMapSetting(
  sb: SupabaseClient,
  projectId: string,
): Promise<{ readonly setting: PortalMapSetting; readonly pack: ProjectPack | null }> {
  const [s, k] = await Promise.all([
    sb
      .from('portal_map_setting')
      .select('hazards_published, landholders_shared, updated_at, updated_by_name')
      .eq('project_id', projectId) as unknown as PromiseLike<Res<SettingRow[]>>,
    sb
      .from('hazard_pack')
      .select('name, version, published_at, published_by_name, stats, hazard_pack_release(audience, version)')
      .eq('project_id', projectId) as unknown as PromiseLike<Res<PackRow[]>>,
  ]);
  if (s.error) throw new Error(plainPortalError(s.error));
  if (k.error) throw new Error(plainPortalError(k.error));
  const row = s.data?.[0];
  const pack = k.data?.[0];
  return {
    setting: {
      hazardsPublished: row?.hazards_published === true,
      landholdersShared: row?.landholders_shared === true,
      updatedAt: row?.updated_at ?? null,
      updatedByName: row?.updated_by_name ?? '',
    },
    pack: pack
      ? {
          name: pack.name,
          version: pack.version,
          publishedAt: pack.published_at,
          publishedByName: pack.published_by_name ?? '',
          properties: typeof pack.stats?.properties === 'number' ? pack.stats.properties : 0,
          clientVersion: pack.hazard_pack_release?.find((r) => r.audience === 'client')?.version ?? null,
        }
      : null,
  };
}

/** Show the client the current hazard pack on their map and in Safety documents (every staff role, decision 1042). */
export async function showCurrentPackToClient(sb: SupabaseClient, projectId: string): Promise<void> {
  const { error } = await sb.rpc('hazard_pack_client_show', { p_project: projectId });
  if (error) throw new Error(plainPortalError(error));
}

/** "Publish hazards to the client portal" and "Show the landholder boundaries to the client" (every staff role). */
export async function setPortalMap(
  sb: SupabaseClient,
  projectId: string,
  hazards: boolean,
  landholders: boolean,
): Promise<PortalMapSetting> {
  const r = (await sb.rpc('portal_map_set', {
    p_project: projectId,
    p_hazards: hazards,
    p_landholders: landholders,
  })) as Res<SettingRow>;
  if (r.error) throw new Error(plainPortalError(r.error));
  const d = r.data!;
  return {
    hazardsPublished: d.hazards_published,
    landholdersShared: d.landholders_shared,
    updatedAt: d.updated_at,
    updatedByName: d.updated_by_name ?? '',
  };
}

/** Teslyn Field's assignments of the project, as staff read them (who the client sees, and when). */
export async function loadProjectAssignments(
  sb: SupabaseClient,
  projectId: string,
): Promise<PortalAssetRow[]> {
  const r = (await sb
    .from('tracker_assignment')
    .select(
      'starts_at, ends_at, tracker_asset:asset_id (id, provider, label, registration, kind, hidden, deleted_at)',
    )
    .eq('project_id', projectId)
    .is('deleted_at', null)) as Res<
    {
      starts_at: string | null;
      ends_at: string | null;
      tracker_asset: {
        id: string;
        provider: string;
        label: string;
        registration: string;
        kind: string;
        hidden: boolean;
        deleted_at: string | null;
      } | null;
    }[]
  >;
  if (r.error) throw new Error(plainPortalError(r.error));
  return (r.data ?? [])
    .filter((x) => x.tracker_asset && !x.tracker_asset.hidden && !x.tracker_asset.deleted_at)
    .map((x) => ({
      asset_id: x.tracker_asset!.id,
      provider: x.tracker_asset!.provider,
      label: x.tracker_asset!.label,
      registration: x.tracker_asset!.registration,
      kind: x.tracker_asset!.kind,
      starts_at: x.starts_at,
      ends_at: x.ends_at,
    }))
    .sort((a, b) => (a.label || a.registration).localeCompare(b.label || b.registration, 'en-AU'));
}
